import type { Reroute } from '@sveltejs/kit/hooks';

const REROUTE_SUBDOMAIN: Record<string, string> = {
  davos: '/wef',
  wef: '/wef',
  shop: '/shop',
  taxi: '/esaf'
};

// Route used for the root path if no subdomain route applies.
const DEFAULT_ROUTE = '/shop';

/**
 * Reroute based on subdomain.
 *
 * If configured, a top level Svelte route (living in src/routes) is reachable
 * by either its canonical route (e.g., https://example.com/wef) or a specific
 * subdomain (e.g., https://wef.example.com/).
 *
 * See also: https://svelte.dev/docs/kit/hooks#Universal-hooks-reroute
 */
export const reroute: Reroute = ({ url }) => {
  // Svelte defaults to url.pathname if reroute() returns undefined. We're just
  // repeating Sveltes default here to make it clearer.
  const canonicalRoute = url.pathname;

  // SvelteKit's own assets (e.g., '/_app/env.js') pass through reroute as
  // well, but only exist at the top level.
  if (url.pathname.startsWith('/_app/')) {
    return canonicalRoute;
  }

  const parts = url.hostname.split('.');

  if (parts.length === 1) {
    // No subdomain, e.g., 'localhost/' -> '/shop'
    return url.pathname === '/' ? DEFAULT_ROUTE : canonicalRoute;
  }

  // e.g., 'foo'
  const subdomain = parts[0];

  if (subdomain in REROUTE_SUBDOMAIN) {
    // e.g., '/foo'
    const baseRoute = REROUTE_SUBDOMAIN[subdomain];

    // e.g., 'foo.example.com/foo' -> '/foo
    if (url.pathname.startsWith(baseRoute)) {
      return canonicalRoute;
    }

    // e.g., 'foo.example.com/' -> '/foo'
    if (url.pathname === '/') {
      return baseRoute;
    }

    // e.g., 'foo.example.com/some-page' -> '/foo/some-page'
    return baseRoute + url.pathname;
  }

  // e.g., 'example.com/' -> '/shop'
  if (url.pathname === '/') {
    return DEFAULT_ROUTE;
  }

  return canonicalRoute;
};
